/**
 * @module Level
 */

import cloneDeep from "lodash/cloneDeep";

import CameraExtended from "./camera-extended.js";
import ComponentManager from "./component-manager.js";
import Component from "./component.js";
import GameObjectTools from "./game-object-tools.js";
import InstanceClass from "./instance-class";
import KeyEmitter from "./key-emitter.js";
import PhysicsLayersManager from "./physics-layer-manager";
import ResourceTypes from "./gf4/resource-types";
import SoundManager from "./sound-manager.js";
import TextAutomation from "./component-text-automation";

const CHILD_COMPONENT_MANAGER = "Child Component Manager";

/**
 * Stand-alone Gamefroot level.
 * @class Level
 * @extends Phaser.Scene
 * @param {Object} config - composite parameter object
 * @param {string} config.key - unique level identifier
 * @param {Object} config.json - data describing the level
 */
const Level = new Phaser.Class( {
    /**
     * @lends Level
     */

    Extends: Phaser.Scene,

    initialize: function Level ( config ) {
        /**
         * Representation of the level as a `gamefroot-4` resource.
         * @readonly
         */
        this.json = config.json;

        Phaser.Scene.call( this, {key: config.key} );
    },

    /**
     * Boilerplate Phaser scene creation function.
     * This will be called every time the scene is restarted.
     */
    create: function () {

        // Initialise level clock properly.
        // It defaults to `Date.now()`, starting at 1970-01-01,
        // but may then update to take 0 as game start time.
        this.time.now = this.game.loop.time;

        /**
         * Table of key objects, used to provide a generic interface
         * for keyboard events.
         * @private
         */
        this._keys = {};
        this.setupKeyboard();

        /**
         * Whether the creation step has been officially completed.
         * This actually happens just before "level start".
         */
        this.created = false;

        this._createSystems();
        this.setupCamera();
        this.setDebug( this.game.debug );
        this.populateLevel();

        this.created = true;

        this.getChildren().forEach( child => {
            if ( child.onLevelStart ) {
                child.onLevelStart();
            }
        } );

        // Set up child updates.
        this._setupUpdateComponents();

        // Prepare for eventual transition to another scene.
        this.events.once( "shutdown", this._shutdown, this );
    },

    /**
     * Perform one-time system setup.
     * @private
     */
    _createSystems: function() {
        if ( !this.components ) {
            /** Component manager for automated updates. */
            this.components = new ComponentManager( this );
        }

        // Set up sound system.
        this.components.add( new SoundManager() );

        /**
         * Physics layer handler
         * @type {PhysicsLayersManager}
         */
        this.physicsLayersManager = new PhysicsLayersManager( this );
    },

    /**
     * Start updating game objects.
     * We do this only once the scene is fully populated,
     * because otherwise we might get overlap between the time points
     * `created` and `started`.
     * @private
     */
    _setupUpdateComponents: function () {

        // Set components to update after standard scene processes.
        // Otherwise, we might get camera adjustments halfway through
        // physics processing.
        this.events.on( "postupdate", this._updateComponents, this );

        this.components.create( {
            name: CHILD_COMPONENT_MANAGER,
            preUpdate: ( time, delta ) => {
                const children = this.getChildren();
                children.forEach( child => {
                    if ( child.components ) {
                        child.components.preUpdate( time, delta );
                    }
                } );
            },
            update: ( time, delta ) => {
                const children = this.getChildren();
                children.forEach( child => {
                    if ( child.components ) {
                        child.components.update( time, delta );
                    }
                } );
            },
            postUpdate: ( time, delta ) => {
                const children = this.getChildren();
                children.forEach( child => {
                    if ( child.components ) {
                        child.components.postUpdate( time, delta );
                    }
                } );

                if ( !this.scene.settings.active ) {
                    return;
                }

                // Update prior frame blocked information.
                // This should be done here, because the collision data
                // for the frame updates before `preUpdate`, but we won't
                // use it after `postUpdate`.
                children.forEach( child => {
                    const map = child.wasBlocked;
                    if ( map ) {
                        const was = child.body.blocked;
                        map.left = was.left;
                        map.right = was.right;
                        map.up = was.up;
                        map.down = was.down;
                        map.none = was.none;
                    }
                } );

                // Perform deferred deletion.
                // We check `exists` strictly against boolean `false`,
                // because an object which naively didn't implement `exists`
                // would otherwise be immediately destroyed.
                this.children.getChildren()
                .filter( child => {
                    return child.active && child.exists === false;
                } )
                .forEach( child => {
                    child.active = false;
                    const instanceClass = this.classes[ child.name ];
                    if ( instanceClass ) {
                        instanceClass.kill( child );
                    } else {
                        if (child.components) {
                            // Trigger `destroy` on components.
                            child.components.removeAll();
                        }
                        child.destroy();
                    }
                } );

                // Drain class pools to preserve performance.
                for ( let key in this.classes ) {
                    this.classes[ key ].drain();
                }

                // Tidy physics layers.
                this.physicsLayersManager.getLayers().forEach( layer => {
                    layer.tidy();
                } );
                const list = this.physics.world.colliders;
                list.getActive().forEach( c => {
                    if ( !c.active ||
                        c.object1.exists === false ||
                        c.object2.exists === false ) {

                        list.remove( c );
                    }
                } );
            },
        } );
    },

    /**
     * Perform critical end-of-session functions.
     * @private
     */
    _shutdown: function () {

        // Remove keyboard interfaces.
        for ( var i in this._keys ) {
            if ( this._keys[ i ].destroy ) {
                this._keys[ i ].destroy();
            }
            delete this._keys[ i ];
        }

        // Stop sound playback.
        const soundManager = this.components.getByName( "SoundManager" );
        if ( soundManager && soundManager.length > 0 ) {
            soundManager[ 0 ].stopAll();
        }

        // Remove components, in case they are invoked during transitions.
        this.components.removeAll();

        // Remove component update listeners.
        this.events.off( "postupdate", this._updateComponents, this );
    },

    /**
     * Update the component manager systems.
     * @private
     */
    _updateComponents: function ( time, delta ) {
        this.components.preUpdate( time, delta );
        this.components.update( time, delta );
        this.components.postUpdate( time, delta );
    },

    /**
     * Boilerplate scene update override.
     * @method update
     * @param {Number} time
     * @param {Number} delta
     */
    update: function ( time, delta ) {

        // Store accessible time delta.
        this.data.set( "delta", delta );
    },

    /**
     * Populate the scene with the contents described in the ResourceStore.
     */
    populateLevel: function () {

        // Validate level.
        if ( !( this.json && this.json.type === ResourceTypes.Level ) ) {
            // Show a user-facing error.
            console.error(
                "Level.populateLevel() error: could not find level data." );
            let error = "Could not find level data.\n" +
                "\n" +
                "A level file may have failed to load. " +
                "Please check your network connection and try again." +
                "\n" +
                "Current level: ";
            if ( this.json ) {
                error += ( this.json && this.json.name ) || "Unknown (data unavailable)";
            } else {
                error += "Unknown (data unavailable)";
            }
            error += "\n" + "Try using the level number: " + this.scene.key;
            this.game.reportError( error );
            return;
        }

        // Set level background color.
        // Expected format: { r: 0-255, g: 0-255, b: 0-255, a: 0-1 }
        const levelColor = this.json.colorRGB;
        this.cameras.main.setBackgroundColor( {
            r: levelColor.r,
            g: levelColor.g,
            b: levelColor.b,
            a: levelColor.a,
        } );

        // Set up classes.

        /**
         * Collection of Groups, each tracking a pool of game objects
         * based on a particular game object class.
         */
        this.classes = {};

        const layers = this.json.layers.map( layer =>
            this.game.resourceStore.getExternalResource(
                this.json, layer
            ) );
        // Instantiate layers, back-to-front.
        layers.forEach(layer => {
            switch ( layer.type ) {
                case ResourceTypes.InstanceLayer:
                    this.addInstanceLayer( layer );
                    break;
                case ResourceTypes.TileLayer:
                    this.addTilemap( layer );
                    break;
                default:
                    console.error( "Skipped invalid layer:", layer );
                    break;
            }
        });
        // Connect Instance variables to scripts.
        // We must do this after all instances are guaranteed to exist.
        const children = this.getChildren();
        children.forEach(child => {
            const resource = child.getData("resource");
            if (!(resource && resource.scripts)) {
                return;
            }
            resource.scripts.forEach(script => {
                // Get Script resource to identify variable types.
                const scriptResource = this.game.resourceStore.getExternalResource(resource, script.id);
                if (!scriptResource) {
                    return;
                }
                const newNames =
                    GameObjectTools.getSafePluginVariableNames(scriptResource);
                // Get instantiated script.
                // This is the safe plugin renamer from the gf4 compiler.
                let pluginName = scriptResource.name.replace(/[^_A-Za-z0-9]/gi, "_") + "_gfbjs";
                if (pluginName[0].match(/[^_A-Za-z]/)) {
                    pluginName = "script_" + pluginName;
                }
                const plugin = child.components.getByName(pluginName)[0];
                if (!plugin) {
                    return;
                }
                // Connect Instance variables.
                script.variables.forEach(variable => {
                    if (typeof variable.value !== "number") {
                        return;
                    }
                    const master = scriptResource.variables.find(
                        srv => srv.name === variable.name);
                    if (!(master && master.varType === "Instance")) {
                        return;
                    }
                    const target = this.game.resourceStore.getExternalResource(
                        resource, variable.value);
                    const instance = children.find(child => {
                        return child.getData("resource").id == target.id;
                    })
                    // Assign!
                    if (instance) {
                        plugin[newNames.get(variable.name)] = instance;
                    }
                });
            });
        });
    },

    /**
     * Set up camera systems for this level.
     * This must be done every time the level is visited,
     * because cameras are cleared on level restart.
     */
    setupCamera: function () {
        const oldCam = this.cameras.main;
        const cam = new CameraExtended(
            0, 0, this.game.config.width, this.game.config.height );
        cam.setScene( this );
        cam.setRoundPixels( true );
        this.cameras.addExisting( cam, true );
        this.cameras.remove( oldCam );
        this.components.addAt( new Component( {
            name: "Camera Manager",
            postUpdate: ( time, delta ) => {
                cam.postUpdate( time, delta );
            },
        } ), 0 );
    },

    /**
     * Set up keyboard systems for this level session.
     * If a key goes down, we immediately record it.
     * This should allow `is key pressed` checks
     * within `any key pressed` event listeners.
     */
    setupKeyboard: function () {
        this.input.keyboard.on( "keydown", event => {
            if ( event && event.keyCode ) {
                this._keys.lastPressed = event.keyCode;
            }
        } );
        this.input.keyboard.on( "keyup", event => {
            if ( event && event.keyCode ) {
                if ( this._keys.lastPressed === event.keyCode ) {
                    delete this._keys.lastPressed;
                }
            }
        } );
    },

    /**
     * Get a safe name for the instance, guaranteed to be unique.
     * @param {Object} instance - `gamefroot-4` SpriteInstance resource
     * @returns {string}
     */
    _getInstanceNameSafe: function ( instance ) {
        // This name is guaranteed unique, because `gamefroot-4` has unique IDs.
        return ( instance.name ? instance.name + "-" : "" ) + instance.id;
    },

    /**
     * Add a child to the level directly after the specified target.
     * This is used to adjust the display order.
     * @param {GameSprite} child - game object to add
     * @param {GameSprite} target - relative game object
     */
    addChildAfter: function ( child, target ) {
        this.addChildRelativeTo( child, target, 1 );
    },

    /**
     * Add a child to the level directly before the specified target.
     * This is used to adjust the display order.
     * @param {GameSprite} child - game object to add
     * @param {GameSprite} target - relative game object
     */
    addChildBefore: function ( child, target ) {
        this.addChildRelativeTo( child, target, 0 );
    },

    /**
     * Add a child to the level, relative to the target.
     * This is used to alter the display list position.
     * @param {GameSprite} child - game object to add
     * @param {GameSprite} target - relative game object
     * @param {Number} relation - index relative to target
     */
    addChildRelativeTo: function ( child, target, relation ) {
        if ( !( child && target ) || child === target ) {
            return;
        }
        const displayList = this.sys.displayList;

        // Remove from original position. Don't invoke callbacks.
        // This assumes that the child is currently a child of this level.
        displayList.remove( child, true );

        // Get insertion locations.
        // Do this after the removal alters the list positions.
        const displayIndex = displayList.getIndex( target );

        // Splice in the addition. Skip callbacks.
        displayList.addAt( child, displayIndex + relation, true );
    },

    /**
     * Add an instance class to the level. Instances are created from classes.
     * @param {Object} instance - `gamefroot-4` SpriteInstance resource
     */
    addInstanceClass: function ( instance ) {
        const template = cloneDeep( instance );
        template.name = this._getInstanceNameSafe( instance );
        this.classes[ template.name ] = new InstanceClass( this, template );
    },

    /**
     * Add the contents of a `gamefroot-4` InstanceLayer resource
     * to this level.
     * @param {Object} layer - serialized layer resource
     */
    addInstanceLayer: function ( layer ) {
        let instances = layer.members.map(
            member => this.game.resourceStore.getExternalResource(
                layer, member ) );

        // Alert and trim bad layers.
        instances.forEach( ( instance, index ) => {
            if ( !( instance && (
                instance.type === ResourceTypes.SpriteInstance ||
                instance.type === ResourceTypes.TextInstance ||
                instance.type === ResourceTypes.ShapeInstanceRectangle) ) )
            {
                console.error(
                    "Could not find instance at index",
                    index, "on layer", layer );
            }
        } );
        instances = instances.filter( instance => instance && (
            instance.type === ResourceTypes.SpriteInstance ||
            instance.type === ResourceTypes.TextInstance ||
            instance.type === ResourceTypes.ShapeInstanceRectangle ) );
        
        // Populate classes.
        instances.filter( instance =>
            instance.type === ResourceTypes.SpriteInstance
        ).forEach( instance => {
            this.addInstanceClass( instance );
        } );

        // Populate level with instances.
        instances.forEach( instance => {
            switch ( instance.type ) {
                case ResourceTypes.SpriteInstance:
                    this.addSpriteByName(
                        this._getInstanceNameSafe( instance ) );
                    break;
                case ResourceTypes.TextInstance:
                    this.addText( instance );
                    break;
                case ResourceTypes.ShapeInstanceRectangle:
                    this.addShapeInstanceRectangle( instance );
                    break;
                default:
                    console.error(
                        "Could not instantiate Instance", instance );
                    break;
            }
        } );
    },

    /**
     * Add a rectangle object to the scene.
     * @param {Object} data - text object description
     * @returns {Phaser.GameObjects.Rectangle}
     */
    addShapeInstanceRectangle: function ( data ) {
        // check data
        let fillColor;
        let fillAlpha;
        let strokeColor;
        let strokeAlpha;
        let strokeWidth;

        if (data.fill) {
            fillColor = data.fill.color;
            fillAlpha = data.fill.alpha;
        }

        if (data.stroke) {
            strokeColor = data.stroke.color;
            strokeAlpha = data.stroke.alpha;
            strokeWidth = data.stroke.width;
        }

        // create object
        const rectangle = this.add.rectangle(
            data.position.x + data.anchor.x,
            data.position.y + data.anchor.y,
            data.size.x,
            data.size.y,
            fillColor,
            fillAlpha,
        );

        // set stroke
        rectangle.setStrokeStyle(strokeWidth, strokeColor, strokeAlpha);

        // set anchor point
        let anchorX = data.anchor.x/data.size.x;
        let anchorY = data.anchor.y/data.size.y;
        if (Number.isNaN(anchorX)) {
            anchorX = 0.5;
        }
        if (Number.isNaN(anchorY)) {
            anchorY = 0.5;
        }
        rectangle.setOrigin( anchorX, anchorY );

        GameObjectTools.setCommonFeatures( rectangle );

        // Customise object.
        rectangle.name = data.name;
        rectangle.rotation = data.rotation;
        rectangle.scaleX = data.scale.x;
        rectangle.scaleY = data.scale.y;
        data.tags.forEach((tag) => {
            rectangle.tags.add(tag);
        });

        rectangle.setupPluginComponents( data );

        // Store gf4 resource.
        rectangle.setData("resource", data);

        rectangle.emit( rectangle.EVENTS.CREATE );

        return rectangle;
    },

    /**
     * Add a sprite to the scene, using the data tagged with
     * the given name.
     * 
     * This is the base of "class" handling in Gamefroot.
     * 
     * @param {String} name - name of source data
     * @returns {GameSprite}
     */
    addSpriteByName: function ( name ) {
        // Get the relevant class definition.
        const instanceClass = this.classes[ name ];
        if ( !instanceClass ) {
            return null;
        }
        return instanceClass.get();
    },

    /**
     * Add a text object to the scene.
     * @param {Object} data - text object description, usually in GF4 format
     * @returns {Phaser.GameObjects.Text}
     */
    addText: function ( data ) {
        if (!isNaN(data.x)) {
            // Data is in abbreviated/GF3 format `{x, y, textText}`.
            // This is passed by some systems such as Blockly.
            data = {
                align: "left",
                anchor: { x: 0, y: 0 },
                color: "#000000",
                fontFamily: "sans-serif",
                fontSize: 32,
                lineHeight: 1,
                lineWidth: Infinity,
                name: "",
                position: { x: data.x, y: data.y },
                rotation: 0,
                scale: { x: 1, y: 1 },
                text: data.textText,
                dependencies: [],
                scripts: [],
                tags: [],
            }
        }

        // Create object.
        const text = this.add.text(
            data.position.x,
            data.position.y,
            data.text,
            {
                fontFamily: data.fontFamily,
                fontSize: data.fontSize,
                color: data.color.replace(/^0x/, "#"),
                align: data.align,
                wordWrap: {
                    width: data.lineWidth,
                },
                padding: data.padding,
            }
        );

        // Align object and anchor.
        let anchorX = data.anchor.x;
        let anchorY = data.anchor.y;
        if ( data.align === "center" ) {
            anchorX += 0.5;
        } else if ( data.align === "right" ) {
            anchorX += 1;
        }
        text.setOrigin( anchorX, anchorY );
        GameObjectTools.setCommonFeatures( text );
        text.anchorX = text.displayOriginX;
        text.anchorY = text.displayOriginY;
        if ( data.align === "center" ) {
            text.posX -= text.width * 0.5;
        } else if ( data.align === "right" ) {
            text.posX -= text.width;
        }

        // Customise object.
        text.name = data.name;
        text.rotation = data.rotation;
        text.scaleX = data.scale.x;
        text.scaleY = data.scale.y;
        text.setLineSpacing( data.lineHeight );
        data.tags.forEach((tag) => {
            text.tags.add(tag);
        });

        // Text doesn't react to physics by default.
        text.setPhysics( false );

        // Setup components.
        text.components.add( new TextAutomation() );
        text.setupPluginComponents( data );

        // Store gf4 resource.
        text.setData("resource", data);

        text.emit( text.EVENTS.CREATE );

        return text;
    },

    /**
     * Add a tilemap to the scene.
     * @param {Object} data - TileLayer resource description
     * @returns {Phaser.Tilemaps.StaticTilemapLayer}
     */
    addTilemap: function ( data ) {
        // Create two-dimensional array.
        const tiles = [];
        for ( let y = 0, len = data.gridSize.y; y < len; y++ ) {
            const row = [];
            tiles.push( row );
            const offset = y * data.gridSize.x;
            for ( let x = 0, len = data.gridSize.x; x < len; x++ ) {
                const tile = data.tiles[ x + offset ];
                if ( tile === undefined ) {
                    tile = 0;
                }
                // Phaser uses -1 for empty tiles, so we de-iterate every tile.
                row.push( tile - 1 );
            }
        }

        const map = this.make.tilemap( {
            data: tiles,
            tileWidth: data.tileSize.x,
            tileHeight: data.tileSize.y,
            width: data.gridSize.x,
            height: data.gridSize.y,
        } );
        const tileset = map.addTilesetImage(
            // Texture with this ID is generated by loader.
            data.id + "-tile-spritesheet",
            undefined,
            data.tileSize.x,
            data.tileSize.y,
            1,
            2,
            -1
        );
        // NOTE: We could add x,y to createLayer,
        // but `Layer` doesn't have `position`.
        const layer = map.createLayer(0, tileset);

        // Make tile layer name accessible at runtime.
        layer.setName(data.name);

        // Add necessary game systems.
        GameObjectTools.setCommonFeatures( layer );
        data.tags.forEach((tag) => {
            layer.tags.add(tag);
        });

        // Set up physics.
        // This only happens if the layer explicitly wants physics.
        if ( data.collides ) {
            layer.collides = true;
            this.physicsLayersManager.addToLayer( layer, 0 );

            // We exclude -1, because while the source files use 0 as empty,
            // Phaser internally seems to use -1.
            // NOTE: I can find no place in Kiwifroot where it uses the
            // `collisions` attribute, so I'll assume that all tilemaps
            // collide.
            // This is probably wrong, as we definitely let tilemaps get
            // turned off.
            // But all the tilemaps in our test game said `collisions=false`
            // and were definitely intended to collide...
            map.setCollisionByExclusion( [ -1 ], true );
        }

        return layer;
    },

    /**
     * Send a message to all scene children, and outside the game.
     * @param {String} message - message identifier
     * @param {Any} [value] - value to send
     */
    broadcast: function ( message, value ) {
        // Send external messages.
        this.messageExternal( message, value );

        // Talk to children.
        this.messageChildren( message, value );
    },

    /**
     * Return all children with the given name.
     * Because `name` is a class identifier, we can shortcut this.
     * @param {String} name - name to search for
     * @returns {Array}
     */
    getAllChildrenByName: function ( name ) {
        const instanceClass = this.classes[ name ];
        if ( !instanceClass ) {
            return [];
        }
        return this.instanceClass.instances;
    },

    /**
     * Get a Phaser anim based on the given sequence.
     * Fetch or create it as appropriate.
     * @param {Object} sequence - `gamefroot-4` Sequence resource
     * @returns {Phaser.Animations.Animation}
     */
    getAnim: function ( sequence ) {
        let anim = this.anims.get( sequence.id );
        if ( anim ) {
            return anim;
        }
        // We must create the anim.
        const frames = sequence.frames.map( frame => {
            const frameResource =
                this.game.resourceStore.getExternalResource( sequence, frame );
            if (frameResource) {
                const textureId =
                    this.game.resourceStore.getFrameImageFile(frameResource).id;
                if (textureId) {
                    // This is what we want:
                    return { key: textureId, frame: 0 };
                } else {
                    console.error("Could not find texture", frameResource.imageId, "on", frameResource);
                }
            } else {
                console.error("Could not find frame", frame, "on", sequence);
            }
            // This is the fallback:
            return { key: "__MISSING", frame: 0 };
        } );
        return this.anims.create( {
            // Coerce to string.
            key: sequence.id + "",
            frames: frames,
            repeat: sequence.repeat,
            frameRate: sequence.frameRate,
        } );
    },

    /**
     * Return the level child with the given ID.
     * @param {Number} id - Game object ID
     * @param {boolean} [recurse=true] - Whether to recurse into groups
     * @returns {GameSprite|null}
     */
    getChildById: function ( id, recurse ) {
        // TODO: Make this able to navigate hierarchical membership.
        // Default behavior should be TRUE.
        const children = this.getChildren();
        for ( let i = 0; i < children.length; i++ ) {
            const child = children[ i ];
            if ( child.getData( "id" ) === id ) {
                return child;
            }
        }
    },

    /**
     * Return the first child with this name.
     * @param {String} name - name to search for
     * @returns {GameSprite}
     */
    getChildByName: function ( name ) {
        const instanceClass = this.classes[ name ];
        if ( instanceClass ) {
            const instances = instanceClass.instances;
            if ( instances.length ) {
                return this.classes[ name ].instances[ 0 ];
            }
        }
        return null;
    },

    /**
     * Return a copy of the display list.
     * This is safe to traverse, even if level membership adjusts
     * during traversal.
     * @returns {array}
     */
    getChildren: function () {
        return this.children.getChildren().filter( child => child.exists );
    },

    /**
     * Return an array of all children with the given tag, in order.
     * This returns an empty array if the level has not finished
     * the `create` step yet, because we don't want any gets
     * at that stage.
     * @param {String} tag - tag to gather
     * @returns {Array}
     */
    getChildrenByTag: function ( tag ) {
        if ( !this.created ) {
            return [];
        }
        const children = this.getChildren();
        return children.filter( child => {
            return child.tags && child.tags.has( tag );
        } );

        // NOTE: We've decided to use this as the basis for several
        // blocks that look for first/last/all tagged objects.

        // NOTE: We may eventually develop a tag system
        // which stores tags in a central map.
        // That would allow us to greatly optimise this method,
        // and any blocks that rely on this kind of list.
    },

    /**
     * Provide an interface for listening to keyboard events.
     * Return an object which emits events from a single key.
     * @param {number} keycode - keycode for the key to provide
     * @returns {KeyEmitter}
     */
    getKey: function ( keycode ) {
        if ( this._keys[ keycode ] ) {
            return this._keys[ keycode ];
        }

        // Create the key and its emitter interface.
        const key = this.input.keyboard.addKey( keycode );
        const emitter = new KeyEmitter( key );
        this._keys[ keycode ] = emitter;
        return emitter;
    },

    /**
     * Return the last child with this name.
     * @param {String} name - name to search for
     * @returns {GameSprite}
     */
    getLastChildByName: function ( name ) {
        const instanceClass = this.classes[ name ];
        if ( instanceClass ) {
            const instances = instanceClass.instances;
            if ( instances.length ) {
                return instances[ instances.length - 1 ];
            }
        }
        return null;
    },

    /**
     * Return a random child with this name.
     * @param {String} name - name to search for
     * @returns {GameSprite}
     */
    getRandomChildByName: function ( name ) {
        const children = this.getAllChildrenByName( name );
        if ( children.length === 0 ) {
            return null;
        }
        return Phaser.Math.RND.pick( children );
    },

    /**
     * Emit events that can be monitored from outside the game.
     * @param {String} message - message to send
     * @param {Any} [value] - contents of message
     */
    messageExternal: function( message, value ) {
        this.game.broadcast.emitter.emit(
            this.game.broadcast.MESSAGE, message, value );
    },

    /**
     * Send a message to all children.
     * @param {String} message - message to send
     * @param {Any} [value] - contents of message
     */
    messageChildren: function( message, value ) {
        this.getChildren().forEach( child => {
            this.messageInstance( child, message, value );
        } );
    },

    /**
     * Send a message to an instance.
     * @param {Phaser.GameObjects.GameObject} instance - target
     * @param {String} message - message to send
     * @param {Any} [value] - contents of message
     */
    messageInstance: function( instance, message, value ) {
        if ( !( instance && instance.properties &&
            instance.properties.set ) ) {
            // Do not message objects that cannot receive messages,
            // e.g. tilemaps.
            return;
        }

        if ( arguments.length > 2 ) {
            // `value` may be `undefined`, so we must count arguments instead.
            instance.properties.set( "_messaging-value_", value );
        }
        instance.properties.set( "_messaging_", message );
    },

    /**
     * Prepare to switch to the target level.
     * Triggered by a listener on `LevelManager.switchTo()`.
     * @param {Number} target - target level index
     */
    onSwitch: function ( target ) {
        this.getChildren().forEach( child => {
            if ( child.onLevelSwitch ) {
                child.onLevelSwitch( target );
            }
        } );
    },

    /**
     * Set debug on or off for this level.
     * @param {Boolean} value - whether to set debug on
     */
    setDebug: function ( value ) {
        const world = this.physics.world;
        if ( value ) {
            // Show everything.
            if ( !world.debugGraphic ) {
                world.createDebugGraphic();
                world.debugGraphic.setData(Phaserfroot.tags.UI, true);
            } else {
                world.debugGraphic.visible = true;
            }
            // TODO: Set children to show appropriate debug.
        } else {
            // Hide everything.
            if ( world.debugGraphic ) {
                world.debugGraphic.visible = false;
            }
            // TODO: Hide child debug elements.
        }
    },

    /**
     * Swap two children in the display list.
     * @param {GameSprite} childA - first child
     * @param {GameSprite} childB - second child
     */
    swapChildren: function ( childA, childB ) {
        const displayList = this.sys.displayList;
        const indexA = displayList.getIndex( childA );
        const indexB = displayList.getIndex( childB );
        if ( indexA < 0 || indexB < 0 ) {
            // Not co-members; abort.
            return;
        }
        displayList.list[ indexA ] = childB;
        displayList.list[ indexB ] = childA;
    }
} );

export default Level;
